import { useEffect, useState } from 'react';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import api from '@/lib/apiClient';
import { showAlert } from '@/components/ui/GlobalAlert';
import CupoIA from '@/components/ai/CupoIA';
import {
  FileSearch, FileEdit, PenLine, Loader2, ShieldAlert,
  Download, Copy, Check, Users, FileText
} from 'lucide-react';

const IC = "w-full bg-white/[0.04] border border-[rgba(201,164,76,0.15)] text-[#F8F6F0] placeholder-[#888]/50 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#C9A44C] focus:ring-1 focus:ring-[#C9A44C]/40 transition-all";

/* ¿El documento es de texto extraíble (Word/HTML/TXT)? */
const esDocTexto = (d) => {
  const m = (d.tipo_mimetype || '').toLowerCase();
  const n = (d.nombre || '').toLowerCase();
  return m.includes('html') || m.includes('text/plain') || m.includes('wordprocessingml')
      || n.endsWith('.html') || n.endsWith('.txt') || n.endsWith('.docx');
};

const TABS = [
  { id: 'analizar', label: 'Analizar expediente', icon: FileSearch },
  { id: 'corregir', label: 'Corregir documento', icon: FileEdit },
  { id: 'redactar', label: 'Redactar documento', icon: PenLine },
];

/* Render markdown mínimo (## ### - **bold**) */
function renderMD(texto) {
  const lineas = (texto || '').split('\n');
  return lineas.map((l, i) => {
    const bold = (s) => s.split(/(\*\*.+?\*\*)/g).map((p, j) =>
      p.startsWith('**') ? <strong key={j} className="text-[#E0C878]">{p.slice(2, -2)}</strong> : p);
    if (l.startsWith('## '))  return <h3 key={i} className="text-[#C9A44C] font-semibold text-base mt-4 mb-1.5" style={{ fontFamily: 'Playfair Display, serif' }}>{l.slice(3)}</h3>;
    if (l.startsWith('### ')) return <h4 key={i} className="text-[#E0C878] font-medium text-sm mt-3 mb-1">{l.slice(4)}</h4>;
    if (l.startsWith('- ') || l.startsWith('* ')) return <li key={i} className="ml-4 list-disc text-[#D8D1C3] text-sm leading-relaxed">{bold(l.slice(2))}</li>;
    if (!l.trim()) return <div key={i} className="h-2" />;
    return <p key={i} className="text-[#D8D1C3] text-sm leading-relaxed">{bold(l)}</p>;
  });
}

export default function AnalisisCasos() {
  const [tab, setTab] = useState('analizar');
  const [expedientes, setExpedientes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [docsAll, setDocsAll] = useState([]);
  const [estado, setEstado] = useState(null);

  const [clienteId, setClienteId] = useState('');
  const [expedienteId, setExpedienteId] = useState('');
  const [documentoId, setDocumentoId] = useState('');
  const [documentoTexto, setDocumentoTexto] = useState('');
  const [docNombre, setDocNombre] = useState('');
  const [cargandoDoc, setCargandoDoc] = useState(false);
  const [instrucciones, setInstrucciones] = useState('');
  const [texto, setTexto] = useState('');
  const [tipo, setTipo] = useState('');

  const [cargando, setCargando] = useState(false);
  const [resultado, setResultado] = useState(null); // {tipo:'md'|'html', contenido, modelo, aviso}
  const [verificado, setVerificado] = useState(false);

  const cargarEstado = () => api.get('/api/casos/estado').then((r) => setEstado(r.data)).catch(() => {});

  useEffect(() => {
    api.get('/api/expedientes/').then((r) => setExpedientes(r.data || [])).catch(() => {});
    api.get('/api/clientes/').then((r) => setClientes(r.data || [])).catch(() => {});
    api.get('/api/vault/evidencias').then((r) => setDocsAll(r.data || [])).catch(() => {});
    cargarEstado();
  }, []);

  const reset = () => { setResultado(null); setVerificado(false); };

  // Expedientes y documentos del cliente elegido
  const expedientesFiltrados = clienteId ? expedientes.filter((e) => e.cliente_id === clienteId) : expedientes;
  const documentos = clienteId ? docsAll.filter((d) => d.cliente_id === clienteId && esDocTexto(d)) : [];

  const elegirCliente = (id) => {
    setClienteId(id);
    setExpedienteId(''); setDocumentoId(''); setDocumentoTexto(''); setDocNombre('');
    reset();
  };

  const elegirDocumento = async (id) => {
    setDocumentoId(id); setDocumentoTexto(''); setDocNombre('');
    if (tab === 'corregir') setTexto('');
    if (!id) return;
    setCargandoDoc(true);
    try {
      const r = await api.get(`/api/vault/evidencias/${id}/texto`);
      setDocumentoTexto(r.data.texto || '');
      setDocNombre(r.data.nombre || '');
      if (tab === 'corregir') setTexto(r.data.texto || '');
      if (r.data.truncado) showAlert('El documento es extenso: se cargó una parte para la IA.', 'warning');
    } catch (e) {
      showAlert(e.response?.data?.detail || 'No se pudo cargar el texto del documento.', 'error');
      setDocumentoId('');
    } finally { setCargandoDoc(false); }
  };

  const cambiarTab = (id) => {
    setTab(id);
    reset();
    // Al pasar a Corregir con un documento ya cargado, precargar el texto.
    if (id === 'corregir' && documentoTexto) setTexto(documentoTexto);
  };

  const ejecutar = async () => {
    reset();
    setCargando(true);
    try {
      let r;
      if (tab === 'analizar') {
        if (!expedienteId && !documentoTexto) { showAlert('Elegí un expediente o un documento del cliente.', 'warning'); setCargando(false); return; }
        r = await api.post('/api/casos/analizar', {
          expediente_id: expedienteId || null,
          instrucciones: instrucciones || null,
          documento: documentoTexto || null,
        });
        setResultado({ tipo: 'md', contenido: r.data.analisis, modelo: r.data.modelo, aviso: r.data.aviso_verificacion });
      } else if (tab === 'corregir') {
        if (texto.trim().length < 10) { showAlert('Elegí un documento del cliente o pegá el texto a revisar.', 'warning'); setCargando(false); return; }
        r = await api.post('/api/casos/corregir', { texto, expediente_id: expedienteId || null });
        setResultado({ tipo: 'md', contenido: r.data.revision, modelo: r.data.modelo, aviso: r.data.aviso_verificacion });
      } else {
        if (!expedienteId && !documentoTexto) { showAlert('Elegí un expediente o un documento base.', 'warning'); setCargando(false); return; }
        if (instrucciones.trim().length < 5) { showAlert('Indicá qué redactar.', 'warning'); setCargando(false); return; }
        r = await api.post('/api/casos/redactar', {
          expediente_id: expedienteId || null,
          tipo: tipo || 'Documento',
          instrucciones,
          documento_base: documentoTexto || null,
        });
        setResultado({ tipo: 'html', contenido: r.data.html, modelo: r.data.modelo, aviso: r.data.aviso_verificacion });
      }
      cargarEstado();
    } catch (e) {
      showAlert(e.response?.data?.detail || 'Error del servicio de IA.', 'error');
    } finally {
      setCargando(false);
    }
  };

  const descargar = () => {
    if (!verificado || !resultado) return;
    const esHtml = resultado.tipo === 'html';
    const cuerpo = esHtml
      ? `<!doctype html><meta charset="utf-8"><body>${resultado.contenido}</body>`
      : resultado.contenido;
    const blob = new Blob([cuerpo], { type: esHtml ? 'text/html' : 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `lexorbit_${tab}_${Date.now()}.${esHtml ? 'html' : 'txt'}`;
    a.click(); URL.revokeObjectURL(url);
  };

  const copiar = () => {
    if (!verificado || !resultado) return;
    const tmp = document.createElement('div'); tmp.innerHTML = resultado.contenido;
    navigator.clipboard.writeText(resultado.tipo === 'html' ? tmp.textContent : resultado.contenido);
    showAlert('Copiado al portapapeles.', 'success');
  };

  const noConfig = estado && !estado.configurado;

  return (
    <DashboardLayout title="Análisis de Casos">
      {/* Cartel de aviso fijo */}
      <div className="flex items-start gap-3 px-4 py-3 mb-5 rounded-xl border border-[#C9A44C]/30 bg-[#C9A44C]/[0.06]">
        <ShieldAlert className="w-5 h-5 text-[#C9A44C] shrink-0 mt-0.5" />
        <p className="text-[13px] text-[#D8D1C3] leading-relaxed">
          <span className="text-[#C9A44C] font-semibold">Aviso:</span> todo lo que genera este panel es asistencia
          por IA. <strong className="text-[#E0C878]">Verificá la información antes de entregarla al cliente o
          presentarla ante un juzgado, organismo o ente.</strong> No reemplaza el criterio del abogado matriculado.
        </p>
      </div>

      {noConfig && (
        <div className="px-4 py-3 mb-5 rounded-xl border border-red-500/30 bg-red-500/[0.07] text-[13px] text-red-300">
          El servicio de IA no está configurado. Falta <code>ANTHROPIC_API_KEY</code> en el servidor.
        </div>
      )}

      <CupoIA data={estado} />

      {/* Tabs */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => cambiarTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all border ${
              tab === id ? 'bg-[#C9A44C]/15 border-[#C9A44C]/40 text-[#E0C878]'
                         : 'border-white/10 text-[#888] hover:text-[#F8F6F0] hover:bg-white/5'}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Columna entrada */}
        <div className="space-y-4">
          {/* Selector de cliente */}
          <div>
            <label className="flex items-center gap-1.5 text-xs text-[#888] mb-1.5">
              <Users className="w-3.5 h-3.5 text-[#C9A44C]" /> Cliente
              <span className="text-[#666]">(para traer sus documentos)</span>
            </label>
            <select value={clienteId} onChange={(e) => elegirCliente(e.target.value)} className={IC}>
              <option value="" className="bg-[#141414]">— Seleccionar cliente —</option>
              {clientes.map((c) => (
                <option key={c.id} value={c.id} className="bg-[#141414]">
                  {c.apellido ? `${c.apellido.toUpperCase()}, ${c.nombre}` : c.nombre}
                </option>
              ))}
            </select>
          </div>

          {/* Selector de expediente (filtrado por cliente) */}
          <div>
            <label className="block text-xs text-[#888] mb-1.5">
              Expediente {tab === 'corregir' && <span className="text-[#666]">(opcional, da contexto)</span>}
            </label>
            <select value={expedienteId} onChange={(e) => setExpedienteId(e.target.value)} className={IC}>
              <option value="" className="bg-[#141414]">— Seleccionar —</option>
              {expedientesFiltrados.map((e) => (
                <option key={e.id} value={e.id} className="bg-[#141414]">{e.caratula}</option>
              ))}
            </select>
          </div>

          {/* Selector de documento del cliente */}
          {clienteId && (
            <div>
              <label className="flex items-center gap-1.5 text-xs text-[#888] mb-1.5">
                <FileText className="w-3.5 h-3.5 text-[#C9A44C]" /> Documento del cliente
                {tab !== 'corregir' && <span className="text-[#666]">(opcional)</span>}
              </label>
              <select value={documentoId} onChange={(e) => elegirDocumento(e.target.value)} className={IC} disabled={cargandoDoc}>
                <option value="" className="bg-[#141414]">— Ninguno —</option>
                {documentos.map((d) => (
                  <option key={d.id} value={d.id} className="bg-[#141414]">
                    {d.nombre}{d.expediente_caratula ? ` · ${d.expediente_caratula}` : ''}
                  </option>
                ))}
              </select>
              {cargandoDoc && (
                <p className="text-xs text-[#888] mt-1.5 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Cargando documento…</p>
              )}
              {!cargandoDoc && documentoTexto && (
                <p className="text-xs text-green-400 mt-1.5 flex items-center gap-1"><Check className="w-3 h-3" /> Cargado: {docNombre}</p>
              )}
              {!cargandoDoc && documentos.length === 0 && (
                <p className="text-xs text-[#666] mt-1.5">Este cliente no tiene documentos de texto (Word, HTML o TXT).</p>
              )}
              {tab === 'analizar' && documentoTexto && (
                <p className="text-[11px] text-[#C9A44C]/80 mt-1">Se analizará este documento.</p>
              )}
              {tab === 'redactar' && documentoTexto && (
                <p className="text-[11px] text-[#C9A44C]/80 mt-1">Se tomará como base para redactar.</p>
              )}
            </div>
          )}

          {tab === 'redactar' && (
            <div>
              <label className="block text-xs text-[#888] mb-1.5">Tipo de documento</label>
              <input value={tipo} onChange={(e) => setTipo(e.target.value)} className={IC}
                placeholder="Ej: Carta documento, Demanda, Contestación, Oficio…" />
            </div>
          )}

          {tab === 'corregir' && (
            <div>
              <label className="block text-xs text-[#888] mb-1.5">
                Documento a revisar {documentoTexto && <span className="text-[#666]">(cargado del documento elegido; podés editarlo)</span>}
              </label>
              <textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={12} className={IC}
                placeholder="Elegí un documento del cliente arriba, o pegá acá el texto que querés revisar…" />
            </div>
          )}

          {(tab === 'analizar' || tab === 'redactar') && (
            <div>
              <label className="block text-xs text-[#888] mb-1.5">
                {tab === 'analizar' ? 'Enfoque (opcional)' : 'Instrucciones'}
              </label>
              <textarea value={instrucciones} onChange={(e) => setInstrucciones(e.target.value)}
                rows={tab === 'redactar' ? 8 : 4} className={IC}
                placeholder={tab === 'analizar'
                  ? 'Ej: Enfocate en los riesgos procesales y plazos…'
                  : 'Ej: Redactá la carta documento intimando el pago en 48hs…'} />
            </div>
          )}

          <button onClick={ejecutar} disabled={cargando || noConfig}
            className="w-full text-[#070707] font-semibold px-4 py-3 rounded-lg flex items-center justify-center gap-2 text-sm disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
            {cargando ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSearch className="w-4 h-4" />}
            {cargando ? 'Procesando…' : 'Ejecutar'}
          </button>
        </div>

        {/* Columna resultado */}
        <div className="rounded-xl border border-white/10 bg-[#0E0E0E] p-5 min-h-[300px]">
          {!resultado && !cargando && (
            <div className="h-full flex items-center justify-center text-center text-[#666] text-sm">
              El resultado aparecerá acá.
            </div>
          )}
          {cargando && (
            <div className="h-full flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-[#C9A44C]" />
            </div>
          )}
          {resultado && (
            <div className="space-y-4">
              <div className="max-h-[55vh] overflow-y-auto pr-1">
                {resultado.tipo === 'html'
                  ? <div className="prose-doc text-[#D8D1C3] text-sm leading-relaxed"
                         dangerouslySetInnerHTML={{ __html: resultado.contenido }} />
                  : <div>{renderMD(resultado.contenido)}</div>}
              </div>

              {/* Aviso + verificación que bloquea la descarga */}
              <div className="border-t border-white/10 pt-4 space-y-3">
                <p className="text-[12px] text-[#C9A44C]/90 leading-relaxed flex gap-2">
                  <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" /> {resultado.aviso}
                </p>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input type="checkbox" checked={verificado} onChange={(e) => setVerificado(e.target.checked)}
                    className="w-4 h-4 accent-[#C9A44C]" />
                  <span className="text-[13px] text-[#D8D1C3]">Verifiqué la información y me hago responsable del contenido.</span>
                </label>
                <div className="flex gap-2">
                  <button onClick={copiar} disabled={!verificado}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs border border-white/10 text-[#aaa] hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed">
                    <Copy className="w-3.5 h-3.5" /> Copiar
                  </button>
                  <button onClick={descargar} disabled={!verificado}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-[#070707] disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ background: verificado ? 'linear-gradient(135deg, #C9A44C, #E0C878)' : '#333' }}>
                    {verificado ? <Download className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />} Descargar
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
